import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Image,
  Flex,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Badge,
} from '@chakra-ui/react';
import {
  FaUsers,
  FaPlus,
  FaInfoCircle,
  FaShieldAlt,
  FaMagic,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DeckList from '../components/Decks/DeckList';
import CreateDeckModal from '../components/Decks/CreateDeckModal';
import { fetchDecks, fetchUserDecks } from '../utils/deckApi';

export default function Decks({ eventsData = [] }) {
  const { userData } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [decks, setDecks] = useState([]);
  const [userDecks, setUserDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'user'
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('lg', 'xl');

  // CIL Colors + Helpers
  const cilColors = {
    yellow: '#ffcc00',
    teal: '#3eb297',
    sky: '#36a9e1',
    pink: '#e95995',
    plum: '#3b235a',
    red: '#e73341',
  };

  const API_URL = process.env.REACT_APP_STRAPI_URL || '';
  const withBase = (url) =>
    url?.startsWith('http') ? url : API_URL ? `${API_URL}${url || ''}` : url;

  const loadDecks = async () => {
    setLoading(true);
    try {
      const [allDecks, userDecksData] = await Promise.all([
        fetchDecks(),
        userData ? fetchUserDecks() : Promise.resolve([]),
      ]);

      setDecks(allDecks);
      setUserDecks(userDecksData);
    } catch (error) {
      toast({
        title: 'Errore nel caricamento',
        description: 'Impossibile caricare i mazzi',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDecks();
  }, [userData]);

  const handleDeckCreated = (newDeck) => {
    setDecks((prev) => [newDeck, ...prev]);
    setUserDecks((prev) => [newDeck, ...prev]);
    toast({
      title: 'Mazzo creato',
      description: 'Il mazzo è stato creato con successo',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleDeckUpdated = (updatedDeck) => {
    setDecks((prev) =>
      prev.map((deck) => (deck.id === updatedDeck.id ? updatedDeck : deck))
    );
    setUserDecks((prev) =>
      prev.map((deck) => (deck.id === updatedDeck.id ? updatedDeck : deck))
    );
    toast({
      title: 'Mazzo aggiornato',
      description: 'Il mazzo è stato aggiornato con successo',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleDeckDeleted = (deckId) => {
    setDecks((prev) => prev.filter((deck) => deck.id !== deckId));
    setUserDecks((prev) => prev.filter((deck) => deck.id !== deckId));
  };

  // Calcola le statistiche dei mazzi
  const getDeckStats = () => {
    const publicDecks = decks.filter((deck) => deck.isPublic !== false);
    const privateDecks = userDecks.filter((deck) => deck.isPublic === false);

    return {
      totalPublic: publicDecks.length,
      totalPrivate: privateDecks.length,
      totalUser: userDecks.length,
      totalAll: publicDecks.length + privateDecks.length, // Totale inclusi i privati dell'utente
    };
  };

  // Ottieni i mazzi da mostrare in base al tab attivo
  const getDecksToShow = () => {
    switch (activeTab) {
      case 'user':
        return userDecks;
      default:
        return decks;
    }
  };

  // Ottieni le statistiche per i badge
  const stats = getDeckStats();

  // Se l'utente non è loggato, mostra solo la hero con bottone "Diventa Socio"
  if (!userData) {
    return (
      <Box>
        {/* HERO SECTION - Solo per utenti non loggati */}
        <Box
          position="relative"
          w="full"
          h="70vh"
          minH="500px"
          overflow="hidden"
          mb={0}
        >
          <Image
            src={
              withBase(
                'https://cms.cilassociazione.it/uploads/carnevale_munlab_d7caee7803.jpg'
              ) || '/fallback-hero.jpg'
            }
            alt="Mazzi Magic - Condividi le tue creazioni con la community CIL"
            w="full"
            h="full"
            objectFit="cover"
          />

          {/* Overlay scuro per migliorare la leggibilità */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
          />

          {/* Contenuto centrato con messaggio di tesseramento */}
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            align="center"
            justify="center"
            direction="column"
            textAlign="center"
            px={6}
          >
            <Heading
              size="4xl"
              color="white"
              mb={6}
              textShadow="2px 2px 4px rgba(0,0,0,0.8)"
              fontWeight="extrabold"
              letterSpacing="wide"
              maxW="800px"
            >
              🃏 Mazzi Magic
            </Heading>

            <Text
              fontSize="xl"
              color="white"
              maxW="600px"
              mb={8}
              textShadow="1px 1px 2px rgba(0,0,0,0.8)"
              fontWeight="medium"
              lineHeight="1.7"
            >
              Per accedere alla piattaforma di condivisione mazzi Magic: The
              Gathering e condividere le tue creazioni con altri soci CIL, devi
              prima diventare socio dell&apos;associazione.
            </Text>

            {/* CTA per tesseramento */}
            <Button
              size="xl"
              bg={cilColors.yellow}
              color={cilColors.plum}
              _hover={{ bg: '#e6b800', transform: 'scale(1.05)' }}
              px={12}
              py={6}
              fontWeight="bold"
              fontSize="lg"
              leftIcon={<FaUsers />}
              transition="all 0.3s"
              onClick={() => navigate('/login')}
            >
              Entra in CIL
            </Button>
          </Flex>
        </Box>
      </Box>
    );
  }

  return (
    <Box bg="transparent" minH="100vh" overflowX="hidden" p={4}>
      <Container maxW="container.xl" py={8}>
        {/* Header */}
        <Box
          bg="whiteAlpha.800"
          boxShadow="md"
          borderRadius="lg"
          px={8}
          py={8}
          mb={6}
          textAlign="center"
        >
          <VStack spacing={4}>
            <Heading
              as="h1"
              size="xl"
              color="purple.600"
              fontFamily="Poppins, sans-serif"
            >
              🃏 Mazzi Magic
            </Heading>

            {/* Istruzioni e Disclaimer */}
            <VStack spacing={4} maxW="4xl">
              <Text
                fontSize="md"
                color="gray.700"
                fontFamily="Montserrat, sans-serif"
                lineHeight="1.6"
              >
                Benvenuto nella piattaforma di condivisione mazzi Magic: The
                Gathering! Qui puoi creare, condividere e scoprire mazzi
                costruiti da altri soci CIL, analizzare le strategie e
                migliorare le tue abilità deckbuilding.
              </Text>

              <HStack spacing={4} justify="center" wrap="wrap">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="purple"
                  leftIcon={<FaInfoCircle />}
                  onClick={() => setIsInstructionsModalOpen(true)}
                  _hover={{ bg: 'purple.50' }}
                >
                  Leggi le Istruzioni
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  leftIcon={<FaShieldAlt />}
                  onClick={() => setIsPrivacyModalOpen(true)}
                  _hover={{ bg: 'orange.50' }}
                >
                  Privacy & Sicurezza
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </Box>

        {/* Navigation Tabs */}
        <Box bg={cardBg} boxShadow={cardShadow} borderRadius="lg" p={3} mb={4}>
          <HStack spacing={3} justify="center" wrap="wrap">
            <Button
              variant={activeTab === 'all' ? 'solid' : 'outline'}
              colorScheme="purple"
              leftIcon={<FaMagic />}
              onClick={() => setActiveTab('all')}
              size="md"
            >
              Tutti i Mazzi
              <Badge ml={2} colorScheme="purple" variant="solid" fontSize="xs">
                {stats.totalAll}
              </Badge>
              {stats.totalPrivate > 0 && (
                <Text ml={1} fontSize="xs" color="gray.500">
                  (di cui {stats.totalPrivate} privati)
                </Text>
              )}
            </Button>
            <Button
              variant={activeTab === 'user' ? 'solid' : 'outline'}
              colorScheme="green"
              leftIcon={<FaUsers />}
              onClick={() => setActiveTab('user')}
              size="md"
            >
              I Miei Mazzi
              <Badge ml={2} colorScheme="green" variant="solid" fontSize="xs">
                {stats.totalUser}
              </Badge>
            </Button>
          </HStack>
        </Box>

        {/* Create Deck Button */}
        <Box textAlign="center" mb={6}>
          <VStack spacing={4} display={{ base: 'flex', md: 'none' }}>
            <Button
              size="lg"
              colorScheme="purple"
              bg="#805ad5"
              color="white"
              leftIcon={<FaPlus />}
              onClick={() => setIsCreateModalOpen(true)}
              _hover={{
                bg: '#6b46c1',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 25px rgba(128, 90, 213, 0.3)',
              }}
              _active={{ bg: '#553c9a' }}
              transition="all 0.3s ease"
              px={8}
              py={6}
              fontWeight="bold"
              fontSize="lg"
              w="100%"
              maxW="300px"
            >
              Crea Nuovo Mazzo
            </Button>
          </VStack>

          {/* Desktop Layout */}
          <HStack
            spacing={6}
            justify="center"
            align="center"
            display={{ base: 'none', md: 'flex' }}
          >
            <Button
              size="lg"
              colorScheme="purple"
              bg="#805ad5"
              color="white"
              leftIcon={<FaPlus />}
              onClick={() => setIsCreateModalOpen(true)}
              _hover={{
                bg: '#6b46c1',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 25px rgba(128, 90, 213, 0.3)',
              }}
              _active={{ bg: '#553c9a' }}
              transition="all 0.3s ease"
              px={8}
              py={6}
              fontWeight="bold"
              fontSize="lg"
            >
              Crea Nuovo Mazzo
            </Button>
          </HStack>
        </Box>

        {/* Content */}
        {loading ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" />
              <Text color="gray.600">Caricamento mazzi...</Text>
            </VStack>
          </Center>
        ) : (
          <DeckList
            decks={getDecksToShow()}
            onDeckUpdated={handleDeckUpdated}
            onDeckDeleted={handleDeckDeleted}
            onDeckCreated={handleDeckCreated}
            userData={userData}
            eventsData={eventsData}
            isUserDecks={activeTab === 'user'}
          />
        )}

        {/* Create Deck Modal */}
        <CreateDeckModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onDeckCreated={handleDeckCreated}
          onDeckUpdated={handleDeckUpdated}
          userData={userData}
        />

        {/* Instructions Modal */}
        <Modal
          isOpen={isInstructionsModalOpen}
          onClose={() => setIsInstructionsModalOpen(false)}
          size="xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack spacing={3}>
                <Icon as={FaInfoCircle} color="purple.500" />
                <Text>📖 Istruzioni per i Mazzi</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Heading size="md" color="purple.600" mb={3}>
                    Come Funziona
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    <Box
                      p={4}
                      bg="purple.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="purple.200"
                    >
                      <Text fontWeight="semibold" color="purple.700">
                        1. 🚀 Crea o Importa il Tuo Mazzo
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        <strong>Crea da zero:</strong> Usa il pulsante
                        &ldquo;Crea Nuovo Mazzo&rdquo; per inserire le carte una
                        per una.
                        <br />
                        <strong>Importa:</strong> Copia e incolla la lista da
                        Moxfield, MTGO, Arena o altre piattaforme.
                      </Text>
                    </Box>

                    <Box
                      p={4}
                      bg="blue.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="blue.200"
                    >
                      <Text fontWeight="semibold" color="blue.700">
                        2. 📊 Analisi Automatica
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        Il sistema analizza automaticamente il tuo mazzo,
                        calcola statistiche e rileva i colori utilizzando
                        l&apos;API di Scryfall.
                      </Text>
                    </Box>

                    <Box
                      p={4}
                      bg="green.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="green.200"
                    >
                      <Text fontWeight="semibold" color="green.700">
                        3. 🎴 Condivisione e Scoperta
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        Condividi i tuoi mazzi con la community CIL e scopri le
                        creazioni di altri soci per ispirazione e miglioramento.
                      </Text>
                    </Box>
                  </VStack>
                </Box>

                <Box>
                  <Heading size="md" color="green.600" mb={3}>
                    Formato Lista Carte
                  </Heading>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • &ldquo;2 Lightning Bolt&rdquo; - 2 copie di Lightning
                      Bolt
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • &ldquo;Lightning Bolt&rdquo; - 1 copia di Lightning Bolt
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • &ldquo;Lightning Bolt (M10)&rdquo; - Lightning Bolt del
                      set M10
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Le righe vuote e i commenti (//) vengono ignorati
                    </Text>
                  </VStack>
                </Box>

                <Box>
                  <Heading size="md" color="blue.600" mb={3}>
                    📥 Importazione da Piattaforme Esterne
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    <Box
                      p={3}
                      bg="blue.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="blue.200"
                    >
                      <Text
                        fontWeight="semibold"
                        color="blue.700"
                        fontSize="sm"
                      >
                        Moxfield
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        Copia la lista delle carte dalla pagina del mazzo e
                        incollala nel campo &ldquo;Lista Carte&rdquo;
                      </Text>
                    </Box>

                    <Box
                      p={3}
                      bg="green.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="green.200"
                    >
                      <Text
                        fontWeight="semibold"
                        color="green.700"
                        fontSize="sm"
                      >
                        MTGO / Arena
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        Esporta la lista dal client e incollala nel campo
                        &ldquo;Lista Carte&rdquo;
                      </Text>
                    </Box>

                    <Box
                      p={3}
                      bg="purple.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="purple.200"
                    >
                      <Text
                        fontWeight="semibold"
                        color="purple.700"
                        fontSize="sm"
                      >
                        Altre Piattaforme
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        La maggior parte delle piattaforme supporta
                        l&apos;esportazione in formato testo standard
                      </Text>
                    </Box>
                  </VStack>
                </Box>

                <Box>
                  <Heading size="md" color="orange.600" mb={3}>
                    Suggerimenti
                  </Heading>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • Inserisci sempre una descrizione della strategia del
                      mazzo
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Seleziona il formato e il bracket appropriati
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • I colori vengono rilevati automaticamente dalle carte
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Usa i filtri per trovare mazzi specifici
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                colorScheme="purple"
                onClick={() => setIsInstructionsModalOpen(false)}
              >
                Ho Capito
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Privacy Modal */}
        <Modal
          isOpen={isPrivacyModalOpen}
          onClose={() => setIsPrivacyModalOpen(false)}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack spacing={3}>
                <Icon as={FaShieldAlt} color="orange.500" />
                <Text>🔒 Privacy & Sicurezza</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Box
                  p={4}
                  bg="orange.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="orange.200"
                >
                  <Text fontWeight="semibold" color="orange.700" mb={2}>
                    Informazioni Visibili
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • <strong>Nome utente:</strong> Visibile a tutti i soci
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • <strong>Nome mazzo:</strong> Visibile a tutti i soci
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • <strong>Lista carte:</strong> Visibile a tutti i soci
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • <strong>Descrizione:</strong> Visibile a tutti i soci
                    </Text>
                  </VStack>
                </Box>

                <Box
                  p={4}
                  bg="blue.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontWeight="semibold" color="blue.700" mb={2}>
                    Come Proteggiamo i Tuoi Dati
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • I mazzi sono accessibili solo ai soci CIL registrati
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Puoi sempre modificare o rimuovere i tuoi mazzi
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Le informazioni personali non vengono condivise
                    </Text>
                  </VStack>
                </Box>

                <Box
                  p={4}
                  bg="green.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="green.200"
                >
                  <Text fontWeight="semibold" color="green.700" mb={2}>
                    Raccomandazioni
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • Condividi solo mazzi che vuoi rendere pubblici
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Non includere informazioni personali nelle descrizioni
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Contatta gli amministratori se hai dubbi sulla privacy
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                colorScheme="orange"
                onClick={() => setIsPrivacyModalOpen(false)}
              >
                Ho Capito
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
}
